import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { queryKeys, CACHE_TIMES, invalidateQueries } from '../lib/queryClient';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 100000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for debugging
api.interceptors.request.use((config) => {
  console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ API Success: ${response.config.url} (${response.status})`);
    return response;
  },
  (error) => {
    console.error(`❌ API Error: ${error.config?.url}`, error.response?.status, error.message);
    return Promise.reject(error);
  }
);

// ==============================================================================
// QUERY HOOKS
// ==============================================================================

// Categories
export const useCategories = () => {
  return useQuery({
    queryKey: queryKeys.categories(),
    queryFn: async () => {
      const { data } = await api.get('/api/categories');
      return data;
    },
    ...CACHE_TIMES.CATEGORIES,
    staleTime: CACHE_TIMES.CATEGORIES.staleTime,
    gcTime: CACHE_TIMES.CATEGORIES.gcTime,
  });
};

// Subcategories
export const useSubcategories = (categorySlug = null) => {
  return useQuery({
    queryKey: categorySlug ? queryKeys.subcategoriesByCategory(categorySlug) : queryKeys.subcategories(),
    queryFn: async () => {
      const params = categorySlug ? `?category=${encodeURIComponent(categorySlug)}` : '';
      const { data } = await api.get(`/api/subcategories${params}`);
      return data;
    },
    ...CACHE_TIMES.SUBCATEGORIES,
  });
};

// Agents list with filters
export const useAgents = (filters = {}) => {
  const { category, subcategory, persona, search, limit = 100 } = filters;
  
  return useQuery({
    queryKey: queryKeys.agentsList(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (subcategory) params.append('subcategory', subcategory);
      if (persona) params.append('persona', persona);
      if (search) params.append('search', search);
      if (limit) params.append('limit', limit.toString());
      
      const { data } = await api.get(`/api/accelerators?${params}`);
      return data;
    },
    ...CACHE_TIMES.AGENTS,
    enabled: true, // Always enabled
  });
};

// Popular accelerators (for homepage)
export const usePopularAccelerators = (limit = 6) => {
  return useQuery({
    queryKey: queryKeys.agentsList({ popular: true, limit }),
    queryFn: async () => {
      const { data } = await api.get(`/api/accelerators?limit=${limit}`);
      return data;
    },
    ...CACHE_TIMES.AGENTS,
  });
};

// Legacy alias for backward compatibility
export const usePopularAgents = usePopularAccelerators;

// Individual agent details
export const useAgent = (agentId) => {
  return useQuery({
    queryKey: queryKeys.agentDetail(agentId),
    queryFn: async () => {
      const { data } = await api.get(`/api/accelerators/${agentId}`);
      return data;
    },
    ...CACHE_TIMES.AGENT_DETAIL,
    enabled: !!agentId, // Only run if agentId is provided
  });
};

// Search agents
export const useAgentSearch = (searchQuery, options = {}) => {
  return useQuery({
    queryKey: queryKeys.agentsSearch(searchQuery),
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      
      const params = new URLSearchParams({
        search: searchQuery,
        limit: options.limit || '20',
      });
      
      const { data } = await api.get(`/api/accelerators?${params}`);
      return data;
    },
    ...CACHE_TIMES.SEARCH,
    enabled: searchQuery && searchQuery.length >= 2, // Only search with 2+ characters
  });
};

// Stats
export const useStats = () => {
  return useQuery({
    queryKey: queryKeys.stats(),
    queryFn: async () => {
      const { data } = await api.get('/api/stats');
      return data;
    },
    ...CACHE_TIMES.STATS,
  });
};

// Config
export const useConfig = (section = null) => {
  return useQuery({
    queryKey: section ? queryKeys.configSection(section) : queryKeys.config(),
    queryFn: async () => {
      const endpoint = section ? `/api/config/${section}` : '/api/config';
      const { data } = await api.get(endpoint);
      console.log ('data from api config',data);
      return data;
    },
    ...CACHE_TIMES.CONFIG,
  });
};

// TechStack
export const useTechStack = () => {
  return useQuery({
    queryKey: queryKeys.techStack(),
    queryFn: async () => {
      const { data } = await api.get('/api/techstack');
      return data;
    },
    ...CACHE_TIMES.CONFIG, // Using config cache time as they're similar
  });
};

// ==============================================================================
// PREFETCH HOOKS

// ==============================================================================
// PREFETCH HOOKS
// ==============================================================================

// Prefetch related data for performance
export const usePrefetchRelatedData = () => {
  const queryClient = useQueryClient();
  
  const prefetchCategories = () => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.categories(),
      queryFn: async () => {
        const { data } = await api.get('/api/categories');
        return data;
      },
      ...CACHE_TIMES.CATEGORIES,
    });
  };
  
  const prefetchPopularAgents = () => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.agentsList({ popular: true, limit: 6 }),
      queryFn: async () => {
        const { data } = await api.get('/api/accelerators?limit=6');
        return data;
      },
      ...CACHE_TIMES.AGENTS,
    });
  };
  
  const prefetchAgentsByCategory = (categorySlug) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.agentsList({ category: categorySlug }),
      queryFn: async () => {
        const { data } = await api.get(`/api/accelerators?category=${encodeURIComponent(categorySlug)}`);
        return data;
      },
      ...CACHE_TIMES.AGENTS,
    });
  };
  
  return {
    prefetchCategories,
    prefetchPopularAgents,
    prefetchAgentsByCategory,
  };
};

// ==============================================================================
// CACHE MANAGEMENT HOOKS
// ==============================================================================

export const useCacheManagement = () => {
  const queryClient = useQueryClient();
  
  const clearAllCache = () => {
    queryClient.clear();
  };
  
  const invalidateAll = () => {
    invalidateQueries.all();
  };
  
  const getCacheStats = () => {
    const queries = queryClient.getQueryCache().getAll();
    return {
      totalQueries: queries.length,
      staleQueries: queries.filter(q => q.isStale()).length,
      fetchingQueries: queries.filter(q => q.isFetching()).length,
      cacheSize: JSON.stringify(queries).length,
    };
  };
  
  return {
    clearAllCache,
    invalidateAll,
    getCacheStats,
    invalidateQueries,
  };
};